import React, { FC, memo, useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Textfit } from 'react-textfit';
import { useButton, useI18n, useLogger } from '~Hooks';
import { useExpressCartActions } from '../../Providers/ExpressCartProvider';
import Icon from '~ComponentsImproved/Icon';
import ItemImage from '../../Components/ItemImage';
import plusIcon from '~Scenes/SelfServiceKiosks/Assets/Icons/plus.svg';
import checkIcon from '../../../../Assets/icons/check.svg';
import arrowForwardIcon from '../../../../Assets/icons/arrow_forward.svg';
import { EposMenuItem } from '../../types';

/** Roughly the widest a card gets in the auto-fill grid. */
const CARD_IMAGE_WIDTH = 380;

/**
 * Image-first card: the photo is the card, title and price sit in a compact
 * footer under it - the description is left to the detail pane. Lifted off the sheet with a soft shadow rather than a border so the
 * images read as the content. The whole card is the tap target.
 */
const Card = styled.div`
  position: relative;
  border-radius: 28px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  user-select: none;
  background: var(--eposSurface);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;

  &.is-touching {
    transform: scale(0.96);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }

  &.is-unavailable {
    opacity: 0.45;
    cursor: default;
  }
`;

/** How long the "Added" confirmation stays on the photo. */
const ADDED_FLASH_MS = 900;

/**
 * Drops over the photo for a moment after an instant add. Confirms the tap
 * landed - a shopper who sees nothing happen either taps again by accident or
 * stops adding. Shown by toggling `is-added` on the card via a ref, no render.
 */
const AddedFlash = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 22px;
  font-weight: 800;
  color: var(--eposButtonText);
  background: var(--eposButton);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-out;

  .is-added & {
    opacity: 0.92;
  }
`;

/** Centered over the image when the item is out of stock. */
const UnavailableBadge = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 10px 20px;
  border-radius: 999px;
  font-size: 18px;
  font-weight: 800;
  white-space: nowrap;
  background: var(--colorBlueGray1200);
  color: var(--colorWhite);
`;

const CardBody = styled.div`
  padding: var(--eposCardBodyPadding);
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

/**
 * Its own row, full width - shrinks to fit via Textfit rather than wrapping or
 * clipping. Textfit only sizes the text to the box; it does not center it, so
 * a short title (bigger font) and a long one (shrunk font) would otherwise sit
 * at different baselines. Flex-centering the fixed-height box keeps every
 * title vertically centered on its row regardless of the size Textfit picks.
 */
const TitleFit = styled(Textfit)`
  width: 100%;
  height: 26px;
  display: flex;
  align-items: center;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: var(--eposText);
`;

/**
 * Below the title: "Add", then the price, then a small icon - plus when the
 * tap adds straight away, an arrow when a detail view opens first. This is the
 * theme's "price button": its background color, white text.
 */
const Badge = styled.div`
  width: 100%;
  margin-top: auto;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 16px;
  background: var(--eposPrice);
  color: var(--eposPriceText);
  font-size: 15px;
  font-weight: 700;
`;

const BadgePriceGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const BadgePrice = styled.span`
  opacity: 0.85;
`;

interface Props {
  item: EposMenuItem;
  /** Navigation only: items that need modifications open the detail pane. */
  onOpenDetail: (item: EposMenuItem) => void;
  /** Out of stock - the card is greyed out and taps are ignored. */
  unavailable: boolean;
}

/**
 * One item card. Instant items go straight into the cart through the cart
 * actions hook - cart actions are never passed down as props.
 */
const ItemCard: FC<Props> = ({ item, onOpenDetail, unavailable }) => {
  const { i18n } = useI18n();
  const logger = useLogger('epos-item-card');
  /** Actions only - a card never re-renders because the cart changed. */
  const cartActions = useExpressCartActions();

  const cardRef = useRef<HTMLDivElement>(null);
  const flashTimerRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(flashTimerRef.current), []);

  /** Class toggle straight on the node - the card never re-renders for the flash. */
  const flashAdded = useCallback(() => {
    const card = cardRef.current;
    if (!card) {
      return;
    }
    card.classList.add('is-added');
    window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(
      () => card.classList.remove('is-added'),
      ADDED_FLASH_MS,
    );
  }, []);

  const handleAdd = useCallback(() => {
    logger.info('Item tapped in menu', { itemId: item.id, instant: item.instant });
    if (item.instant) {
      cartActions.addMenuItem(item);
      flashAdded();
      return;
    }
    onOpenDetail(item);
  }, [item, cartActions, onOpenDetail, flashAdded]);
  const { touching, disabled, disabledToast, ...button } = useButton({
    onClick: handleAdd,
    disabled: unavailable,
  });

  const className = [touching && 'is-touching', unavailable && 'is-unavailable']
    .filter(Boolean)
    .join(' ');

  return (
    <Card
      {...button}
      role="button"
      className={className || undefined}
      data-testid="epos-item-card"
      data-item-id={item.id}
      data-instant={item.instant}>
      {/* Square top corners - the card itself clips to its own radius. The
          flash class lives on this wrapper, which React never re-classes. */}
      <div ref={cardRef}>
        <ItemImage
          imageUrl={item.imageUrl}
          cdnWidth={CARD_IMAGE_WIDTH}
          borderRadius={0}
          grayscale={unavailable}>
          {unavailable && <UnavailableBadge>{i18n('Express.Item.Unavailable')}</UnavailableBadge>}
          {item.instant && (
            <AddedFlash>
              <Icon src={checkIcon} color="var(--eposButtonText)" width="28px" height="28px" />
              {i18n('Express.Item.Added')}
            </AddedFlash>
          )}
        </ItemImage>
      </div>

      <CardBody>
        <TitleFit mode="single" max={20} min={12} forceSingleModeWidth={false}>
          {item.title}
        </TitleFit>
        <Badge>
          {i18n('App.Add')}
          <BadgePriceGroup>
            <BadgePrice>{item.price}</BadgePrice>
            <Icon
              src={item.instant ? plusIcon : arrowForwardIcon}
              color="var(--eposPriceText)"
              width="14px"
              height="14px"
            />
          </BadgePriceGroup>
        </Badge>
      </CardBody>
    </Card>
  );
};

export default memo(ItemCard);
